import React, { useEffect, useRef, useState } from "react";
// import { ScopeCertificateDetails } from '../../../sc-models/scope-certificate-details-dto';
import {
  ICertifiedOrganizationDetails,
  initialCertifiedOrganizationDetails,
  mapCertifiedOrganizationDetailsApiResponseToInitialValues,
  mapPayload,
} from "../../../sc-models/certified-organization-details-dto";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
// import { API } from '../../../../../../shared/app-configs/sc-app-config';
import useNotification from "../../../../../../shared/hooks/useNotification";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { Box } from "@mui/material";
import { buyerSellerDetailsService } from "../../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import {
  buildCertifiedOrganizationValidationSchema,
  isNaLike,
} from "../../utils/scValidation";
import { useScStore } from "../../../../../../store/scStandard.store";


interface CertifiedOrganizationDetailsProps {
  onNextTab: () => Promise<void>;
  scStatus?: boolean;
}

const CertifiedOrganizationDetails: React.FC<
  CertifiedOrganizationDetailsProps
> = ({ onNextTab, scStatus }) => {
  const formRef = useRef<any>(null);
  const { loading, executeGet, setLoadingState, executePost } = useApi();

  const { notify } = useNotification();
  const navigate = useNavigate();
  const { formtype, SC_ID } = useParams();
  const hasCreateSC = formtype === "create-sc";
  const hasSubmitSC: boolean = formtype === "submited-sc";

  const [visible, setVisible] = useState({
    certifiedOrganizationTEId: true,
    certifiedOrganizationName: true,
    coTEIdLabel: true,
  });

  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [disable, setDisable] = useState({
    address1: true,
    country: true,
    state: true,
    town: true,
    postcode: true,
  });

  const getCountry = async () => {
    try {
      const result = await dtrackItServiceV4.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
    } catch (err) {
      notify("Failed to fetch countries", "error");
    }
  };

  const getState = async (countryCode: string) => {
    try {
      setIsLoadingStates(true);
      const result = await dtrackItServiceV4.getStateBasedonCountry(countryCode);
      const states = result.data
        .filter((t: any) => !countryCode.includes(t.code_1)) // Do not include the country itself as a state
        .map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
      if (states.length === 0) {
        setCountryOptions((prevCountryOptions: any) => {
          const cleanCountryCode = countryCode.replace(/['\[\]]/g, '');
          const countryOption = prevCountryOptions.find((c: any) => c.value === cleanCountryCode);
          if (countryOption) {
            setStateOptions([countryOption]);
          } else {
            setStateOptions([]);
          }
          return prevCountryOptions;
        });
      } else {
        setStateOptions(states);
      }
    } catch (err) {
      setStateOptions([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const [initialValues, setInitialValues] =
    useState<ICertifiedOrganizationDetails>(
      initialCertifiedOrganizationDetails
    );
  const [scNo, setScNo] = useState<string>("");

  const validateTeIdMatching = (coTeId: string, scNumber: string): string | null => {
    if (!coTeId || !scNumber) return null;
    
    // Extract 8 digits from TE-ID (e.g. TE-00675956 -> 00675956)
    const coMatch = coTeId.match(/^TE-(\d{8})$/);
    if (!coMatch) return "Invalid Certified Organization TE-ID format. Must be TE-########";
    const coTeIdNum = coMatch[1];
    
    // Extract 8 digits from SC Number
    let scTeIdNum = "";
    const scMatch = scNumber.match(/SC-(\d{8})/i);
    if (scMatch) {
      scTeIdNum = scMatch[1];
    } else {
      const scFallbackMatch = scNumber.match(/\d{8}/);
      if (scFallbackMatch) {
        scTeIdNum = scFallbackMatch[0];
      }
    }
    
    if (scTeIdNum && coTeIdNum !== scTeIdNum) {
      const cleanScNo = scNumber.includes('SC-') ? scNumber.substring(scNumber.indexOf('SC-')) : scNumber;
      return `The TE-ID in Certified Organization (${coTeId}) must match the TE-ID embedded in the Scope Certificate Number (${cleanScNo}).`;
    }
    
    return null;
  };

  const fetchCoDetails = async () => {
    try {
      const response: any = await executeGet(API_ROUTES.GET_CO_DETAILS_V4, {
        scId: SC_ID,
      });
      Utills.cl("This is the response", response);
      const data: any =
        mapCertifiedOrganizationDetailsApiResponseToInitialValues(
          response.data
        );
      setInitialValues(data);

      let fetchedScNo = "";
      const cachedScData = useScStore.getState().scData;
      if (cachedScData?.scNo) {
        fetchedScNo = cachedScData.scNo;
      } else if (SC_ID) {
        try {
          const scResponse = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
          if (scResponse?.data?.scNo) {
            fetchedScNo = scResponse.data.scNo;
          }
        } catch (scErr) {
          console.error("Failed to fetch SC Details for scNo:", scErr);
        }
      }
      setScNo(fetchedScNo);

      // Move this check here to ensure it happens after data is loaded
      if (data.certifiedOrganizationTeId) {
        setVisible((prev) => ({
          ...prev,
          coTEIdLabel: false,
          certifiedOrganizationName: false,
        }));
      }

      // Sync disable flags based on presence of values from API
      const isPresent = (v: any) => v !== null && v !== undefined && v !== "";
      const isTownPresent =
        data.certifiedOrganizationTown !== null &&
        data.certifiedOrganizationTown !== undefined &&
        data.certifiedOrganizationTown !== "" &&
        data.certifiedOrganizationTown !== "Not Available";
      setDisable({
        address1: isPresent(data.certifiedOrganizationAddress1),
        country: isPresent(data.certifiedOrganizationCountryOrArea),
        state: isPresent(data.certifiedOrganizationStateOrProvince),
        town: isTownPresent,
        postcode: isPresent(data.certifiedOrganizationPostcode),
      });

      // Ensure country code is formatted like TE handlers: "['IN']"
      if (data.certifiedOrganizationCountryOrArea) {
        const countryCode = JSON.stringify(
          `[${[data.certifiedOrganizationCountryOrArea].map((item: any) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
      }
    } catch (err) {
      Utills.cl("I am in catch block");
      console.error("Failed to fetch CO Base Details:", err);
    }
  };

  useEffect(() => {
    getCountry();
    if (SC_ID || !hasCreateSC) {
      fetchCoDetails();
    }
  }, [SC_ID]);

  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      Utills.cl(
        `fetchTEDetallsFromAPI details, query ${JSON.stringify(
          queryParams
        )} response data ${JSON.stringify(data)}`
      );

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error;
    }
  };

  const handleSellerTEIdValueChange = (value: any) => {
    const fields = [
      "certifiedOrganizationAddress1",
      "certifiedOrganizationTown",
      "certifiedOrganizationPostcode",
      "certifiedOrganizationStateOrProvince",
      "certifiedOrganizationCountryOrArea",
      "certifiedOrganizationName",
      "certifiedOrganizationTeId",
      "certifiedOrganizationNativeName",
      "certifiedOrganizationCbClientNo",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationName: true,
        coTEIdLabel: true,
      }));
    } else {
      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationName: false,
      }));
      formRef.current.setFieldValue(
        "certifiedOrganizationName",
        value.account_name
      );
      formRef.current.setFieldValue("certifiedOrganizationTeId", value.TE_ID);
      formRef.current.setFieldValue("certifiedOrganizationNativeName", "");
      formRef.current.setFieldValue("certifiedOrganizationCbClientNo", "");
      // formRef.current.setFieldValue(
      //   "certifiedOrganizationAddress1",
      //   value.street
      // );
      // Guard against null city â€” Yup.string() rejects null; coerce to empty string
      formRef.current.setFieldValue("certifiedOrganizationTown", value.city ?? "");
      formRef.current.setFieldValue(
        "certifiedOrganizationPostcode",
        value.postal_code
      );

      if (
        value.country != null &&
        value.country != "" &&
        value.country != undefined
      ) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
      }
      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          country: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationCountryOrArea", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          country: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationCountryOrArea",
          value.country
        );
      }

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationAddress1",
          value.street
        );
      }

      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          state: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationStateOrProvince", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          value.state
        );
      }

      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationPostcode",
          value.postal_code
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationPostcode", "");
      }

      // Handle town
      if (value.city != null && value?.city !== 'Not Available') {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", "");
      }

      setVisible((prev) => ({
        ...prev,
        // certifiedOrganizationName: false,
        coTEIdLabel: false,
        certifiedOrganizationName: false,
      }));
    }
  };

  const handleSellerNameValueChange = (value: any) => {
    const fields = [
      "certifiedOrganizationAddress1",
      "certifiedOrganizationTown",
      "certifiedOrganizationPostcode",
      "certifiedOrganizationStateOrProvince",
      "certifiedOrganizationCountryOrArea",
      "certifiedOrganizationName",
      "certifiedOrganizationTeId",
      "certifiedOrganizationNativeName",
      "certifiedOrganizationCbClientNo",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });

      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationTEId: true,
      }));
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
    } else {
      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationName: false,
      }));
      formRef.current.setFieldValue(
        "certifiedOrganizationName",
        value.account_name
      );
      formRef.current.setFieldValue("certifiedOrganizationTeId", value.TE_ID);
      formRef.current.setFieldValue("certifiedOrganizationNativeName", "");
      formRef.current.setFieldValue("certifiedOrganizationCbClientNo", "");
      // formRef.current.setFieldValue(
      //   "certifiedOrganizationAddress1",
      //   value.street
      // );
      // Guard against null city â€” Yup.string() rejects null; coerce to empty string
      formRef.current.setFieldValue("certifiedOrganizationTown", value.city ?? "");
      formRef.current.setFieldValue(
        "certifiedOrganizationPostcode",
        value.postal_code
      );

      //handle certifiedOrganizationAddress1
      if (value.street === null ||
        value.street === "" ||
        value.street === undefined) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationAddress1",
          value.street
        );
      }

      // Handle country
      if (
        value.country != null &&
        value.country != "" &&
        value.country != undefined
      ) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
        setDisable((prev) => ({
          ...prev,
          country: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationCountryOrArea",
          value.country
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          country: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationCountryOrArea", "");
      }

      // Handle state
      if (value.state != null) {
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          value.state
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          state: false,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          ""
        );
      }

      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationPostcode",
          value.postal_code
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationPostcode", "");
      }

      // Handle town
      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", "");
      }

      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationTEId: false,
      }));
    }
  };
  const formConfig = {
    pageName: "",
    initialValues,
    validationSchema: buildCertifiedOrganizationValidationSchema(),
    readOnly: hasSubmitSC || scStatus,
    sections: [
      {
        title: "Certified Organization Information",
        fields: [
          {
            label: "Certified Organization TE-ID",
            name: "certifiedOrganizationTeId",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationTeId,
            visible: (value: any) => visible.certifiedOrganizationTEId,
            props: {
              placeholder: "Search TE-ID",
              variant: "outlined",
              param: "certifiedOrganizationTeId",
              restrictInputFormat: visible.coTEIdLabel,
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "TE-ID",
            name: "certifiedOrganizationTeId",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            // value: 'initialValues.sellerAddress1',
            // disabled: (values: any) => disable.address1,
            visible: (value: any) => !visible.certifiedOrganizationTEId,
            props: {
              placeholder: "Enter TE-ID",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Name",
            name: "certifiedOrganizationName",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationName,
            visible: (value: any) => true,
            props: {
              placeholder: "Search Name",
              variant: "outlined",
              param: "certifiedOrganizationName",
              onChange: handleSellerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Name",
            name: "certifiedOrganizationName",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled: true,
            visible: (value: any) => false,
            props: {
              placeholder: "Enter Name",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Street Address",
            name: "certifiedOrganizationAddress1",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationAddress1,
            // disabled: true,
            disabled: (values: any) => disable.address1,
            props: { placeholder: "Enter Address Line 1" },
          },
          {
            label: "Certified Organization Town",
            name: "certifiedOrganizationTown",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues?.certifiedOrganizationTown,
            helperText: 'Please fill "na" if town is not applicable',
            disabled: (values: any) => disable.town,
            props: { placeholder: "Enter Town" },
          },
          {
            label: "Certified Organization Postcode",
            name: "certifiedOrganizationPostcode",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationPostcode,
            disabled: (values: any) => disable.postcode,
            helperText: 'Please fill "na" if no postcode is not applicable',
            props: { placeholder: "Enter Postcode" },
          },
          {
            label: "Certified Organization Native Name",
            name: "certifiedOrganizationNativeName",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            helperText: 'Please fill "na" if no native name is not applicable',
            props: { placeholder: "Enter Native Name" },
          },
          {
            label: "Certified Organization Country/Area",
            name: "certifiedOrganizationCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationCountryOrArea,
            disabled: (values: any) => disable.country,
            events: {
              onChange: (newValue: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode);
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: "Select Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization State/Province",
            name: "certifiedOrganizationStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationStateOrProvince,
            disabled: (values: any) => disable.state,
            props: {
              options: stateOptions,
              enableSearch: true,
              placeholder: "Select State/Province",
              fullWidth: true,
              loading: isLoadingStates,
            },
          },
          {
            label: "Certified Organization CB Client Number",
            name: "certifiedOrganizationCbClientNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            props: {
              placeholder: "Enter Client Number of certified organization",
            },
          },
          // {
          //   label: "Contact Person First Name",
          //   name: "certifiedOrganizationContactFirstName",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Contact Person First Name" },
          // },
          // {
          //   label: "Contact Person Last Name",
          //   name: "certifiedOrganizationContactLastName",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Contact Person Last Name" },
          // },
          // {
          //   label: "Website",
          //   name: "certifiedOrganizationWebsite",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Website" },
          // },
          // {
          //   label: "Public Email",
          //   name: "certifiedOrganizationEmail",
          //   type: "chips",
          //   gridSize: { xs: 12, md: 12, lg: 12 },
          //   required: false,
          //   props: {
          //     placeholder: "Enter Email",
          //     fullWidth: true,
          //   },
          // },
          // {
          //   label: "Display Certified org public email on Public Listing",
          //   name: "certifiedOrganizationPublicEmail",
          //   type: "radio",
          //   gridSize: { xs: 12, md: 12, lg: 12 }, // Changed to full width
          //   required: false,
          //   value: initialValues.certifiedOrganizationPublicEmail,
          //   props: {
          //     options: [
          //       { value: "Y", label: "Yes" },
          //       { value: "N", label: "No" },
          //     ],
          //   },
          // },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      try {
        if (SC_ID) {
          // Normalize ALL NA variants → 'na' before saving
          const normalizeNa = (v?: string) =>
            v && isNaLike(v.trim()) ? 'na' : (v || '');
          const normalizedValues = {
            ...values,
            certifiedOrganizationName: normalizeNa(values.certifiedOrganizationName),
            certifiedOrganizationNativeName: normalizeNa(values.certifiedOrganizationNativeName),
            certifiedOrganizationAddress1: normalizeNa(values.certifiedOrganizationAddress1),
            certifiedOrganizationTown: normalizeNa(values.certifiedOrganizationTown),
            certifiedOrganizationPostcode: normalizeNa(values.certifiedOrganizationPostcode),
          };

          const teIdError = validateTeIdMatching(normalizedValues.certifiedOrganizationTeId, scNo);
          if (teIdError) {
            notify(teIdError, "error");
            return;
          }

          setLoadingState(true);
          const payload = mapPayload(
            normalizedValues,
            SC_ID,
            countryOptions,
            stateOptions
          );
          const result: any = await executePost(API_ROUTES.CO_DETAILS_V4, payload);
          if (result?.status == 200) {
            notify("Saved Succesfully", "success");
            await onNextTab();
            navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`);
          } else {
            notify("Unable to Save", "error");
            setLoadingState(false);
          }
        }
      } catch (error) {
        setLoadingState(false);
        console.error("Error saving Certified Organization Details:", error);
      }
    },
  };

  // Handle form submission outside of FormBuilderV4
  const handleSubmit = async () => {
    if (formRef.current) {
      const formik = formRef.current;
      // submitForm() always increments Formik's submitCount even when validation fails.
      // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
      // If validation passes, it also calls the formConfig.handleSubmit which saves + navigates.
      await formik.submitForm();
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        notify("Please fill in all required fields before proceeding", "error");
      }
    }
  };

  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`);
  };

  return (
    <Box>
      {/* Render the FormBuilderV4 component and pass formRef and onSubmit handler */}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilderV4
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />

          {/* Submit button outside the FormBuilderV4 */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <NavLink to="/v4/sc-dashboard">
              <StyledButton sx={{ boxShadow: "none" }}>
                Home
              </StyledButton>
            </NavLink>
            <StyledButton
              variant="contained"
              sx={{ boxShadow: "none" }}
              onClick={hasSubmitSC ? handleNavigateToNextTab : handleSubmit}
            >
              {hasSubmitSC ? "Next" : "Save & Next"}
            </StyledButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CertifiedOrganizationDetails;
